import { supabase } from './supabase'
import { toDayKey } from '../utils/dayKey'
import type { Debtor, DebtorBalance, DebtorEntry } from '../types'

/**
 * Accounts the till keeps for people who pay later.
 *
 * Supabase-only, like services/activation.ts: the feature is gated on a flag
 * that only exists in Postgres, so a Firebase counterpart would be code that
 * can never run.
 *
 * Balances are computed here rather than stored. Every screen that shows one
 * reads the entries and adds them up, see `balancesFrom`, because a stored
 * total and the entries behind it drift apart exactly when it matters, and
 * then neither can be trusted over the other.
 */

type Row = Record<string, unknown>

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback)

function toDebtor(r: Row): Debtor {
  return {
    id: str(r.id),
    name: str(r.name),
    phone: str(r.phone) || undefined,
    note: str(r.note) || undefined,
    active: r.active !== false,
    discountPercent: r.discount_percent != null && Number(r.discount_percent) > 0 ? Number(r.discount_percent) : undefined,
  }
}

function toEntry(r: Row): DebtorEntry {
  return {
    id: str(r.id),
    debtorId: str(r.debtor_id),
    kind: r.kind === 'payment' ? 'payment' : r.kind === 'discount' ? 'discount' : 'debt',
    // bigint can arrive as a string; Number covers both without losing the
    // integer at the sizes a restaurant deals in.
    amountKurus: Number(r.amount_kurus ?? 0),
    orderId: str(r.order_id) || undefined,
    description: str(r.description) || undefined,
    dayKey: str(r.day_key),
    createdAtLocal: Number(r.created_at_local ?? 0),
    createdByName: str(r.created_by_name) || undefined,
  }
}

/**
 * People and what each one owes, from the raw rows.
 *
 * Exported so the screens and the tests use the same arithmetic, a balance
 * worked out twice is a balance that can be worked out two ways.
 */
export function balancesFrom(debtors: Debtor[], entries: DebtorEntry[]): DebtorBalance[] {
  const totals = new Map<string, { balance: number; last: number }>()

  for (const e of entries) {
    const current = totals.get(e.debtorId) ?? { balance: 0, last: 0 }
    current.balance += e.kind === 'debt' ? e.amountKurus : -e.amountKurus
    current.last = Math.max(current.last, e.createdAtLocal)
    totals.set(e.debtorId, current)
  }

  return debtors.map((d) => {
    const t = totals.get(d.id)
    return { ...d, balanceKurus: t?.balance ?? 0, lastMovedAt: t?.last ?? 0 }
  })
}

/**
 * Everyone's accounts, live.
 *
 * Both tables in one subscription because neither is useful alone: a list of
 * people with no balances is a phone book, and entries with no names are
 * numbers. One channel also means one re-render when a sale is put on an
 * account, rather than two that briefly disagree.
 */
export function subscribeDebtors(
  tenantId: string,
  onData: (list: DebtorBalance[], entries: DebtorEntry[]) => void,
): () => void {
  if (!supabase) {
    onData([], [])
    return () => {}
  }

  let cancelled = false

  const read = async () => {
    const [people, moves] = await Promise.all([
      supabase!.from('debtors').select('*').eq('tenant_id', tenantId),
      supabase!.from('debtor_entries').select('*').eq('tenant_id', tenantId),
    ])

    if (cancelled) return

    if (people.error || moves.error) {
      console.error('[cari] okunamadı', people.error ?? moves.error)
      return
    }

    const debtors = (people.data ?? []).map((r) => toDebtor(r as Row))
    const entries = (moves.data ?? []).map((r) => toEntry(r as Row))
    onData(balancesFrom(debtors, entries), entries)
  }

  void read()

  // Unique channel name for the same reason as tenantSettings: Supabase keeps
  // one channel per name and refuses a second subscribe on it, so a remount
  // would throw.
  const channel = supabase
    .channel(`cari:${tenantId}:${crypto.randomUUID()}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'debtors', filter: `tenant_id=eq.${tenantId}` },
      () => void read(),
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'debtor_entries',
        filter: `tenant_id=eq.${tenantId}`,
      },
      () => void read(),
    )
    .subscribe()

  return () => {
    cancelled = true
    void supabase!.removeChannel(channel)
  }
}

export async function saveDebtor(
  tenantId: string,
  debtor: Omit<Debtor, 'id'> & { id?: string },
): Promise<string> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')

  const name = debtor.name.trim()
  if (!name) throw new Error('İsim boş olamaz.')

  // Generated here, not by the database: the till has to know the id before
  // the write lands, the same reason orders carry client-made ids.
  const id = debtor.id ?? crypto.randomUUID()

  const { error } = await supabase.from('debtors').upsert({
    id,
    tenant_id: tenantId,
    name,
    phone: debtor.phone?.trim() ?? '',
    note: debtor.note?.trim() ?? '',
    active: debtor.active,
    discount_percent: debtor.discountPercent ?? null,
    updated_at: new Date().toISOString(),
  })

  if (error) {
    // 23505 on debtors_name_uniq, someone by this name already has an account.
    if (error.code === '23505') throw new Error('Bu isimde bir kayıt zaten var.')
    throw new Error(error.message)
  }
  return id
}

export async function deleteDebtor(tenantId: string, id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase.from('debtors').delete().eq('tenant_id', tenantId).eq('id', id)
  if (error) {
    // The guard trigger raises foreign_key_violation for anyone with history.
    if (error.code === '23503') {
      throw new Error('Bu kişinin hareketleri var. Silmek yerine pasife alın.')
    }
    throw new Error(error.message)
  }
}

/**
 * Writes one movement.
 *
 * Entries are never edited or removed, a debt written by mistake is undone
 * by a movement the other way, which is why the amount is always positive and
 * the direction is a separate field. A ledger that can be rewritten is not a
 * ledger, the same reason orders are voided rather than deleted.
 */
export interface NewEntry {
  debtorId: string
  kind: DebtorEntry['kind']
  amountKurus: number
  orderId?: string
  description?: string
  createdByName?: string
}

export async function addEntry(tenantId: string, entry: NewEntry): Promise<void> {
  return addEntries(tenantId, [entry])
}

/**
 * Birden çok hareket, tek yazımda: indirimli tahsilat (tahsilat + indirim)
 * ya hep birlikte kaydedilir ya hiç. Yarısı kaydedilmiş bir indirimli
 * tahsilat bakiyeyi yanlış bırakırdı.
 */
export async function addEntries(tenantId: string, entries: NewEntry[]): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  for (const e of entries) {
    if (!Number.isInteger(e.amountKurus) || e.amountKurus <= 0) {
      throw new Error('Tutar sıfırdan büyük olmalı.')
    }
  }
  const now = Date.now()
  const { error } = await supabase.from('debtor_entries').insert(
    entries.map((e, i) => ({
      id: crypto.randomUUID(),
      tenant_id: tenantId,
      debtor_id: e.debtorId,
      kind: e.kind,
      amount_kurus: e.amountKurus,
      order_id: e.orderId ?? null,
      description: e.description ?? '',
      day_key: toDayKey(),
      // Aynı anda yazılanlar geçmişte sabit sırada dursun.
      created_at_local: now + i,
      created_by_name: e.createdByName ?? '',
    })),
  )

  if (error) throw new Error(error.message)
}
